// src/components/ui/GradientHeading.tsx (or wherever you keep shared UI components)

import clsx from 'clsx'; // Using clsx for cleaner class name merging (optional but recommended)
import React from 'react';
// Install: npm install clsx  OR  yarn add clsx

interface GradientHeadingProps {
  /** The heading level (1 for h1, 2 for h2, 3 for h3) */
  level?: 1 | 2 | 3;
  /** The content to display inside the heading */
  children: React.ReactNode;
  /** Optional additional Tailwind classes */
  className?: string;
}

const GradientHeading: React.FC<GradientHeadingProps> = ({
  level = 2,
  children,
  className = '', // Default to empty string if no className is provided
}) => {
  // Determine the HTML tag ('h1', 'h2', 'h3') based on the level prop
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Tag = `h${level}` as any;

  // Define base styles applicable to all heading levels
  const baseStyles = 'font-bold';

  // Define the gradient classes and the text clipping effect
  const gradientClasses =
    'bg-gradient-to-r from-green-600 to-blue-600 dark:from-purple-500 dark:to-indigo-600';
  // These classes create the gradient text effect:
  // 1. `bg-gradient-to-r ...`: Applies the background gradient.
  // 2. `bg-clip-text`: Clips the background to the shape of the text.
  // 3. `text-transparent`: Makes the actual text color transparent, revealing the gradient behind it.
  const textEffectClasses = 'bg-clip-text text-transparent';

  // Define styles specific to each heading level (adjust sizes as needed)
  let levelStyles = '';
  switch (level) {
    case 1:
      levelStyles = 'text-4xl md:text-5xl lg:text-6xl'; // Example sizes for H1
      break;
    case 2:
      levelStyles = 'text-3xl md:text-4xl'; // Example sizes for H2 (similar to your original)
      break;
    case 3:
      levelStyles = 'text-2xl md:text-3xl'; // Example sizes for H3
      break;
    // No default needed due to TypeScript constraint (level is 1, 2, or 3)
  }

  // Combine all classes using clsx for better conditional class handling
  // Order matters slightly: specific styles override broader ones if needed.
  const combinedClasses = clsx(
    baseStyles,
    levelStyles,
    gradientClasses,
    textEffectClasses,
    className, // Allows adding custom classes from props
  );

  return <Tag className={combinedClasses}>{children}</Tag>;
};

export default GradientHeading;
